import { useState, useRef, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal, Eye, EyeOff } from 'lucide-react'
import { videos } from '../api/client'

interface VideoMenuProps {
  videoId: string
  isWatched: boolean
  onWatchedChange?: (watched: boolean) => void
}

export default function VideoMenu({ videoId, isWatched, onWatchedChange }: VideoMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const watchedMutation = useMutation({
    mutationFn: (watched: boolean) => videos.setWatched(videoId, watched),
    onSuccess: (_, watched) => {
      queryClient.invalidateQueries({ queryKey: ['videos', 'watched'] })
      queryClient.invalidateQueries({ queryKey: ['movies'] })
      queryClient.invalidateQueries({ queryKey: ['series'] })
      onWatchedChange?.(watched)
    },
  })

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  const handleToggleWatched = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    watchedMutation.mutate(!isWatched)
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={handleToggle}
        className="p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
        title="More options"
      >
        <MoreHorizontal size={18} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-dark-800 border border-dark-700 rounded-lg shadow-xl z-50 py-1">
          <button
            onClick={handleToggleWatched}
            disabled={watchedMutation.isPending}
            className="w-full px-4 py-2 text-left text-sm text-dark-200 hover:bg-dark-700 flex items-center gap-3"
          >
            {isWatched ? (
              <>
                <EyeOff size={16} />
                Mark as unwatched
              </>
            ) : (
              <>
                <Eye size={16} />
                Mark as watched
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
}
